import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { addBaseProductDataAction, requestAvailabilityData, requestPriceData } from "~/domains/product/redux/actions";
import { trackPdpLoad } from "~/app/analytics/ga/ga";
import { loadCartAmounts } from "~/features/addToCart/redux/cartAmounts/actions";
import { type RootState } from "~/app/store";
import { isKITProduct } from "~/domains/product/types/ProductData";
import { userIsSalesman } from "~/domains/user/utils/roles";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export const PDP_SELECT_PRICE_REFERENCE = "PDP_SELECT_PRICE_REFERENCE";

export function loadPDPData(product: PdpPageProductData, priceReference: string): ThunkResult<void> {
  return async (dispatch, getState) => {
    const isSalesman = userIsSalesman();
    const productCode = product.code;
    dispatch(addBaseProductDataAction(product, true));

    // Initially load the product for default price reference
    const pricePromise = dispatch(requestPriceData(productCode, priceReference));
    const availabilityPromise = dispatch(requestAvailabilityData(productCode));

    const baseProduct = getState().products.basic[productCode];

    if (isKITProduct(baseProduct)) {
      if (baseProduct.optionalParts.length > 0) {
        baseProduct.optionalParts.forEach((product) => {
          dispatch(requestPriceData(product.product.code, priceReference));
          dispatch(requestAvailabilityData(product.product.code));
        });
      }

      if (baseProduct.includedParts.length > 0) {
        baseProduct.optionalParts.forEach((product) => {
          dispatch(requestPriceData(product.product.code, priceReference));
          dispatch(requestAvailabilityData(product.product.code));
        });
      }
    }

    if (!isSalesman) {
      let price;
      try {
        const prices = await pricePromise;
        price = prices.length === 1 ? prices[0] : undefined;
      } catch (err) {
        price = undefined;
      }
      let availability;
      try {
        availability = await availabilityPromise;
      } catch (err) {
        availability = undefined;
      }

      trackPdpLoad(baseProduct, price, availability, priceReference);
    }

    dispatch(loadCartAmounts());
  };
}

export function selectPriceReference(productCode: string, priceReference: string): ThunkResult<void> {
  return (dispatch) => {
    dispatch(requestPriceData(productCode, priceReference));
    dispatch(selectPriceReferenceAction(priceReference));
  };
}

function selectPriceReferenceAction(priceReference: string) {
  return {
    type: PDP_SELECT_PRICE_REFERENCE,
    payload: priceReference,
  } as const;
}

export type PdpActions = ReturnType<typeof selectPriceReferenceAction>;
